import { groupBy, keyBy, partition } from 'es-toolkit';

import { FileFromBrowserUpload } from './files/FileFromBrowserUpload';
import { UploadedFile } from './files/UploadedFile';
import { ComponentFileCollection, FileProcessingResult, MiscellaneousFileCollection, Region } from './types';
import { appendNumber, getFullLowercaseName } from './utilities/fileProcessingUtilities';
import {
  findAllFilesWithExtension,
  findFileByExtension,
  findFileByName,
  isAlpineJob,
  isMiTekJob,
  isMiTekJobComponent,
  isSimpsonJob,
  isSimpsonJobComponent,
} from './utilities/jobFileList';

export async function processFiles(
  fileDataArrayList: FileList | readonly UploadedFile[],
  regions?: readonly Region[],
  replace = false,
): Promise<FileProcessingResult> {
  const fileDataArray =
    fileDataArrayList instanceof FileList ? FileFromBrowserUpload.fromList(fileDataArrayList) : fileDataArrayList;

  if (isMiTekJob(fileDataArray)) {
    return processMiTekJob(fileDataArray, replace);
  }
  if (isSimpsonJob(fileDataArray)) {
    return processSimpsonJob(fileDataArray);
  }
  if (isAlpineJob(fileDataArray)) {
    return processAlpineJob(fileDataArray);
  }
  if (replace) {
    return processForSinglePDFs(fileDataArray);
  }
  return {
    componentDesigns: {},
    miscellaneousFiles: miscellaneousFilesToObject(fileDataArray),
  };
}

function processForSinglePDFs(fileDataArray: readonly UploadedFile[]): FileProcessingResult {
  const componentDesigns: ComponentFileCollection = {};
  const miscellaneousFiles: UploadedFile[] = [];

  // Group by component design name - the 5-2a of 5-2a.pdf and 5-2a.tre
  const componentFiles = groupBy(fileDataArray, (file) => file.filename.toLowerCase());
  normalizeComponentAndMetaPdfNames(componentFiles);
  for (const componentName of Object.keys(componentFiles)) {
    const pdfEntry = findFileByExtension(componentFiles[componentName], 'pdf');
    // Only return component designs that have a PDF and a TRE at minimum
    if (pdfEntry != null && componentFiles[componentName].length === 1) {
      componentDesigns[componentName] = componentFiles[componentName];
    } else {
      miscellaneousFiles.push(...componentFiles[componentName]);
    }
  }
  const miscellaneousFilesObject = miscellaneousFilesToObject(miscellaneousFiles);
  return { componentDesigns, miscellaneousFiles: miscellaneousFilesObject };
}

/**
 * @summary Redo the keys to be just the filename instead of the fully qualified path: cj-2aa instead of demo/cj-2aa
 */
export function normalizeComponentAndMetaPdfNames(
  fileCollection: ComponentFileCollection | MiscellaneousFileCollection,
) {
  for (const originalName of Object.keys(fileCollection)) {
    const normalizedFilename = determineComponentOrMetaPdfName(originalName, fileCollection);

    if (normalizedFilename !== originalName) {
      fileCollection[normalizedFilename] = fileCollection[originalName];
      delete fileCollection[originalName];
    }
  }
}

function determineComponentOrMetaPdfName(
  fullyQualifiedFilename: string,
  otherFiles: ComponentFileCollection | MiscellaneousFileCollection,
): string {
  if (!fullyQualifiedFilename.includes('/')) {
    return fullyQualifiedFilename;
  }

  const fullyQualifiedFilenameSegments = fullyQualifiedFilename.split('/');
  const baseFileName = fullyQualifiedFilenameSegments[fullyQualifiedFilenameSegments.length - 1];
  let normalizedComponentName = baseFileName;

  let i = 1;
  while (otherFiles[normalizedComponentName] != null) {
    normalizedComponentName = appendNumber(baseFileName, i);
    i += 1;
  }

  return normalizedComponentName;
}

export function getNameFromMitekDesignFile(file: UploadedFile, fileDataArray: readonly UploadedFile[]) {
  if (
    file.filetype?.toLowerCase() === 'pdf' &&
    file.filename.slice(-4).toUpperCase() === '-ENG' &&
    // A PDF with the "-Eng" suffix is considered the main PDF as long as there is not another PDF with the base name
    !fileDataArray.find(
      (f) =>
        f.filetype?.toLowerCase() === 'pdf' && f.filename.toUpperCase() === file.filename.slice(0, -4).toUpperCase(),
    )
  ) {
    return file.filename.slice(0, -4).toLowerCase();
  }
  return file.filename.toLowerCase();
}

function processMiTekJob(fileDataArray: readonly UploadedFile[], replace = false): FileProcessingResult {
  const componentDesigns: ComponentFileCollection = {};
  const miscellaneousFiles: UploadedFile[] = [];

  // Group by component design name - the 5-2a of 5-2a.pdf and 5-2a.tre
  const componentFiles = groupBy(fileDataArray, (uploadedFile) =>
    getNameFromMitekDesignFile(uploadedFile, fileDataArray),
  );
  normalizeComponentAndMetaPdfNames(componentFiles);

  let hasShownAnsWarning = false;

  for (const componentName of Object.keys(componentFiles)) {
    const pdfEntry = findFileByExtension(componentFiles[componentName], 'pdf');
    const treEntry = findFileByExtension(componentFiles[componentName], 'tre');
    const ansEntry = findFileByExtension(componentFiles[componentName], 'ans');

    if (!hasShownAnsWarning && pdfEntry && treEntry && !ansEntry) {
      hasShownAnsWarning = true;
      alert('Please select a PDF, TRE, and ANS file for each component');
    }

    // Valid MiTek components must have a PDF, TRE, and ANS file at minimum
    if (pdfEntry && treEntry && ansEntry) {
      const [designFiles, otherFiles] = partition(componentFiles[componentName], isMiTekJobComponent);
      componentDesigns[componentName] = designFiles;
      miscellaneousFiles.push(...otherFiles);
    } else if (replace && pdfEntry && componentFiles[componentName].length === 1) {
      componentDesigns[componentName] = componentFiles[componentName];
    } else {
      miscellaneousFiles.push(...componentFiles[componentName]);
    }
  }

  const miscellaneousFilesObject = miscellaneousFilesToObject(miscellaneousFiles);

  return { componentDesigns, miscellaneousFiles: miscellaneousFilesObject, plateManufacturer: 'MiTek' };
}

async function processAlpineJob(fileDataArray: readonly UploadedFile[]): Promise<FileProcessingResult> {
  const ofEntries = findAllFilesWithExtension(fileDataArray, 'of');
  const pdfEntries = findAllFilesWithExtension(fileDataArray, 'pdf');

  const componentDesigns: ComponentFileCollection = {};

  const possibleComponentNames = await Promise.all(ofEntries.map(pParseOFFile));

  // Some of files might be empty, so ignore those (causing them to become misc files)
  const foundComponentNames = possibleComponentNames.filter((component) => component != null) as string[][];

  for (const [componentName, internalAlpineName] of foundComponentNames) {
    const pdfFile = pdfEntries.find((pdf) => isPdfForComponent(pdf.filename, componentName));
    const designFile = findFileByName(fileDataArray, internalAlpineName, 'of');

    if (pdfFile == null || designFile == null) {
      continue;
    }

    componentDesigns[componentName] = [pdfFile, designFile];
  }

  const componentDesignFiles = Object.values(componentDesigns).flat();
  const miscellaneousFilesArray = fileDataArray.filter((file) => !componentDesignFiles.includes(file));
  const miscellaneousFiles = miscellaneousFilesToObject(miscellaneousFilesArray);

  return { componentDesigns, miscellaneousFiles, plateManufacturer: 'Alpine' };
}

function processSimpsonJob(fileDataArray: readonly UploadedFile[], replace = false): FileProcessingResult {
  const componentDesigns: ComponentFileCollection = {};
  const miscellaneousFiles: UploadedFile[] = [];

  const componentFiles = groupBy(fileDataArray, (file) => file.filename.toLowerCase());
  normalizeComponentAndMetaPdfNames(componentFiles);

  for (const componentName of Object.keys(componentFiles)) {
    const pdfEntry = findFileByExtension(componentFiles[componentName], 'pdf');
    const tdlTrussEntry = findFileByExtension(componentFiles[componentName], 'tdlTruss');
    if (pdfEntry != null && tdlTrussEntry != null) {
      const [designFiles, otherFiles] = partition(componentFiles[componentName], isSimpsonJobComponent);
      componentDesigns[componentName] = designFiles;
      miscellaneousFiles.push(...otherFiles);
    } else if (replace && componentFiles[componentName].length === 1 && pdfEntry != null) {
      componentDesigns[componentName] = componentFiles[componentName];
    } else {
      miscellaneousFiles.push(...componentFiles[componentName]);
    }
  }

  const miscellaneousFilesObject = miscellaneousFilesToObject(miscellaneousFiles);

  return {
    componentDesigns,
    miscellaneousFiles: miscellaneousFilesObject,
    plateManufacturer: 'Simpson',
  };
}

function miscellaneousFilesToObject(miscellaneousFilesArray: readonly UploadedFile[]): MiscellaneousFileCollection {
  const result = keyBy(miscellaneousFilesArray, getFullLowercaseName);
  normalizeComponentAndMetaPdfNames(result);

  return result;
}

function isPdfForComponent(pdfName: string, componentName: string) {
  return pdfName.includes('Calcs') && pdfName.toLowerCase().includes(`-${componentName.toLowerCase()}-`);
}

async function pParseOFFile(file: UploadedFile) {
  const nameParts = file.filename.split('/');
  const internalAlpineName = nameParts[nameParts.length - 1];

  const contents = await file.getContents().then(parseBase64DataUrlToASCII);
  if (contents === '') {
    console.log(`skipping ${internalAlpineName}`);
    return null;
  }

  const componentName = extractComponentNameFromOFFile(contents);
  return [componentName, internalAlpineName];
}

function parseBase64DataUrlToASCII(base64: string) {
  return atob(base64.split(',')[1].slice(0, -2));
}

function extractComponentNameFromOFFile(fileContents: string) {
  const componentNameLineParts = fileContents.split('\n')[9].split(':');

  if (componentNameLineParts[0] !== 'Truss Description') {
    throw new Error('Invalid OF file encountered: component name not found on expected line');
  }

  return componentNameLineParts[1].trim();
}
